import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus, FiX, FiEdit2, FiTrash2, FiChevronLeft, FiChevronRight,
  FiArrowUp, FiArrowDown, FiArrowLeft, FiCheck, FiMoreHorizontal, FiFileText,
} from 'react-icons/fi';
import ModalHistoricoCliente from './ModalHistoricoCliente';
import {
  listarGrupos, criarGrupo, atualizarGrupo, excluirGrupo,
  listarColunasDoGrupo, criarColuna, atualizarColuna, excluirColuna, criarItem, atualizarItem, excluirItem,
  listarClientesDoGrupo, vincularClientesAoGrupo, desvincularClienteDoGrupo,
  definirVinculoClienteColuna, buscarExecucao, marcarExecucao,
} from '../../services/gruposFluxo';
import { listarObrigacoesNibo, listarDepartamentosNiboAPI } from '../../services/rotinas';
import { listarClientes as listarNossosClientes } from '../../services/clientes';
import { buscarMinhasPermissoes } from '../../services/permissoes';
import styles from './GruposFluxo.module.css';

/* tipo do usuário logado (admin/gerente/usuario) -- só admin e gerente
   podem vincular/trocar o setor de um grupo e enxergam todos os setores;
   um usuário comum só vê os grupos do próprio setor (filtrado no backend). */
function useMeuTipo() {
  const [tipo, setTipo] = useState(null);
  useEffect(() => {
    buscarMinhasPermissoes().then(p => setTipo(p.tipo)).catch(() => setTipo('usuario'));
  }, []);
  return tipo;
}

function useFecharComEsc(onClose) {
  useEffect(() => {
    function aoTeclar(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [onClose]);
}

function competenciaAtual() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function deslocarCompetencia(comp, n) {
  let [y, m] = comp.split('-').map(Number);
  m += n;
  while (m > 12) { m -= 12; y += 1; }
  while (m < 1)  { m += 12; y -= 1; }
  return `${y}-${String(m).padStart(2, '0')}`;
}
function rotuloCompetencia(comp) {
  const [y, m] = comp.split('-');
  return `${m}/${y}`;
}

/* O Painel abria SEMPRE no mês corrente -- quem trabalha a competência
   anterior (ex.: Folha fechando agosto durante setembro) voltava pra
   agosto, marcava, e na próxima vez que a tela montava (recarregar, trocar
   de aba, ir em Tarefas e voltar) caía em setembro com tudo "em aberto",
   parecendo que as bolinhas tinham desmarcado sozinhas. As marcações
   estavam salvas certinho, só em outro mês. Agora lembra, por grupo, a
   competência escolhida por último -- mas só dentro do mesmo mês do
   calendário em que ela foi escolhida: na virada do mês volta pro mês
   corrente, pra ninguém ficar preso numa competência velha sem perceber. */
const CHAVE_COMPETENCIA_PAINEL = 'painel.competenciaPorGrupo';
function lerCompetenciasLembradas() {
  try { return JSON.parse(localStorage.getItem(CHAVE_COMPETENCIA_PAINEL)) || {}; } catch { return {}; }
}
function competenciaLembrada(grupoId) {
  const lembrada = lerCompetenciasLembradas()[grupoId];
  return lembrada && lembrada.escolhidaEm === competenciaAtual() ? lembrada.competencia : competenciaAtual();
}
function lembrarCompetencia(grupoId, competencia) {
  if (!grupoId) return;
  try {
    const todas = lerCompetenciasLembradas();
    todas[grupoId] = { competencia, escolhidaEm: competenciaAtual() };
    localStorage.setItem(CHAVE_COMPETENCIA_PAINEL, JSON.stringify(todas));
  } catch { /* sem localStorage (aba anônima etc.) -- só não lembra */ }
}
/* largura da coluna Cliente (Rotinas), arrastando a divisão entre ela e a
   primeira obrigação -- fica salva no aparelho (não é por grupo, é uma
   preferência de tela única) e sobrevive a fechar/reabrir o navegador,
   já que localStorage não é apagado ao "reiniciar" nada, só se o usuário
   limpar os dados do site ou usar aba anônima. */
const CHAVE_LARGURA_CLIENTE = 'painel.larguraColunaCliente';
const LARGURA_CLIENTE_PADRAO = 260;
const LARGURA_CLIENTE_MIN = 140;
const LARGURA_CLIENTE_MAX = 640;
function larguraClienteLembrada() {
  try {
    const v = Number(localStorage.getItem(CHAVE_LARGURA_CLIENTE));
    return v >= LARGURA_CLIENTE_MIN && v <= LARGURA_CLIENTE_MAX ? v : LARGURA_CLIENTE_PADRAO;
  } catch { return LARGURA_CLIENTE_PADRAO; }
}
function lembrarLarguraCliente(largura) {
  try { localStorage.setItem(CHAVE_LARGURA_CLIENTE, String(Math.round(largura))); } catch { /* sem localStorage -- só não lembra */ }
}

function rotuloVencimento(iso) {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

/* ══════════════════════════ Componente raiz ══════════════════════════ */

function GruposFluxo() {
  const [vista, setVista] = useState({ tipo: 'lista' });

  if (vista.tipo === 'editor') {
    return (
      <EditorGrupo
        grupoResumo={vista.grupo}
        onVoltar={() => setVista({ tipo: 'lista' })}
      />
    );
  }
  return <ListaGrupos onAbrirGrupo={grupo => setVista({ tipo: 'editor', grupo })} />;
}

/* ══════════════════════════ Lista de grupos ══════════════════════════ */

function ListaGrupos({ onAbrirGrupo }) {
  const [grupos, setGrupos]         = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro]             = useState('');
  const [modalNovoAberto, setModalNovoAberto] = useState(false);

  function carregar() {
    setCarregando(true);
    listarGrupos()
      .then(r => setGrupos(r.items || []))
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }
  useEffect(() => { carregar(); }, []);

  async function excluir(grupo, e) {
    e.stopPropagation();
    if (!window.confirm(`Excluir o grupo "${grupo.nome}"? Isso apaga a linha do tempo, os vínculos de clientes e todo o histórico de execução dele.`)) return;
    try {
      await excluirGrupo(grupo.id);
      carregar();
    } catch (err) {
      setErro(err.message);
    }
  }

  async function alternarAtivo(grupo, e) {
    e.stopPropagation();
    try {
      await atualizarGrupo(grupo.id, { ativo: !grupo.ativo });
      carregar();
    } catch (err) {
      setErro(err.message);
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.listaHeader}>
        <div>
          <h2 className={styles.listaTitulo}>Grupos de fluxo</h2>
          <p className={styles.listaSub}>Cada grupo define uma linha do tempo e os clientes acompanhados por ela.</p>
        </div>
        <button type="button" className={styles.btnPrimario} onClick={() => setModalNovoAberto(true)}>
          <FiPlus size={14} /> Novo grupo
        </button>
      </div>

      {erro && <p className={styles.empty}>{erro}</p>}

      {carregando ? (
        <p className={styles.empty}>Carregando...</p>
      ) : grupos.length === 0 ? (
        <p className={styles.empty}>Nenhum grupo cadastrado ainda. Clique em "Novo grupo" pra criar o primeiro.</p>
      ) : (
        <table className={styles.tabelaGrupos}>
          <thead>
            <tr>
              <th>Nome</th>
              <th style={{ width: 90 }}>Colunas</th>
              <th style={{ width: 80 }}>Itens</th>
              <th style={{ width: 130 }}>Clientes</th>
              <th style={{ width: 90 }}>Status</th>
              <th style={{ width: 70 }}></th>
            </tr>
          </thead>
          <tbody>
            {grupos.map(g => (
              <tr key={g.id} onClick={() => onAbrirGrupo(g)}>
                <td>
                  <div className={styles.nomeGrupo}>{g.nome}</div>
                  {g.descricao && <div className={styles.descGrupo}>{g.descricao}</div>}
                </td>
                <td>{g.totalColunas}</td>
                <td>{g.totalItens}</td>
                <td>{g.totalClientes}</td>
                <td>
                  <span className={`${styles.statusPill} ${g.ativo ? styles.ativo : styles.inativo}`}>
                    {g.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td>
                  <div className={styles.acoesGrupo}>
                    <button type="button" className={styles.btnFantasma} title={g.ativo ? 'Desativar' : 'Ativar'} onClick={e => alternarAtivo(g, e)}>
                      {g.ativo ? <FiX size={14} /> : <FiCheck size={14} />}
                    </button>
                    <button type="button" className={styles.btnFantasma} title="Excluir" onClick={e => excluir(g, e)}>
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modalNovoAberto && (
        <ModalNovoGrupo
          grupos={grupos}
          onClose={() => setModalNovoAberto(false)}
          onCriado={grupo => { setModalNovoAberto(false); onAbrirGrupo(grupo); }}
        />
      )}
    </div>
  );
}

function ModalNovoGrupo({ grupos, onClose, onCriado }) {
  const [nome, setNome]           = useState('');
  const [descricao, setDescricao] = useState('');
  const [vincularId, setVincularId] = useState('');
  const [copiarClientes, setCopiarClientes] = useState(false);
  const [setorId, setSetorId]     = useState('');
  const [salvando, setSalvando]   = useState(false);
  const [erro, setErro]           = useState('');
  const tipo = useMeuTipo();
  const podeDefinirSetor = tipo === 'admin' || tipo === 'gerente';
  const [departamentos, setDepartamentos] = useState([]);
  useFecharComEsc(onClose);

  useEffect(() => {
    if (podeDefinirSetor) listarDepartamentosNiboAPI().then(r => setDepartamentos(r.items || [])).catch(() => {});
  }, [podeDefinirSetor]);

  async function salvar(e) {
    e.preventDefault();
    if (!nome.trim()) return;
    setSalvando(true); setErro('');
    try {
      const setorEscolhido = departamentos.find(d => d.id === setorId);
      const grupo = await criarGrupo({
        nome: nome.trim(),
        descricao: descricao.trim() || null,
        vincular_grupo_id: vincularId || null,
        copiar_clientes: vincularId ? copiarClientes : false,
        ...(podeDefinirSetor ? { setor_nibo_id: setorId || null, setor_nome: setorEscolhido?.name || null } : {}),
      });
      onCriado(grupo);
    } catch (err) {
      setErro(err.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="modalOverlayGF" style={overlayStyle}>
      <form style={modalBoxStyle} onClick={e => e.stopPropagation()} onSubmit={salvar} className={styles.wrap}>
        <h3 style={{ margin: '0 0 14px', fontSize: 16 }}>Novo grupo</h3>

        <Campo label="Nome do grupo">
          <input autoFocus value={nome} onChange={e => setNome(e.target.value)} placeholder='Ex.: "FOLHA - PRO-LABORE"' style={inputStyle} />
        </Campo>
        <Campo label="Descrição (opcional)">
          <textarea value={descricao} onChange={e => setDescricao(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
        </Campo>
        <Campo label="Vincular linha do tempo (opcional)">
          <select value={vincularId} onChange={e => setVincularId(e.target.value)} style={inputStyle}>
            <option value="">Deixar em branco — linha do tempo vazia</option>
            {grupos.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
          </select>
          <p style={{ fontSize: 11, color: '#93a1b3', margin: '4px 0 0' }}>
            Copia colunas, itens e ordem do grupo escolhido. A cópia é independente — mudanças aqui não afetam o original.
          </p>
        </Campo>
        {vincularId && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 4 }}>
            <input type="checkbox" checked={copiarClientes} onChange={e => setCopiarClientes(e.target.checked)} />
            Copiar também os clientes vinculados
          </label>
        )}
        {podeDefinirSetor && (
          <Campo label="Setor (opcional)">
            <select value={setorId} onChange={e => setSetorId(e.target.value)} style={inputStyle}>
              <option value="">Sem setor — visível a todo mundo</option>
              {departamentos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <p style={{ fontSize: 11, color: '#93a1b3', margin: '4px 0 0' }}>
              Só quem for desse setor (ou admin/gerente) vai enxergar esse grupo em Etapas e Auditoria.
            </p>
          </Campo>
        )}

        {erro && <p style={{ color: '#d0453b', fontSize: 12 }}>{erro}</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className={styles.btnSecundario} onClick={onClose}>Cancelar</button>
          <button type="submit" className={styles.btnPrimario} disabled={salvando || !nome.trim()}>
            {salvando ? 'Criando...' : 'Criar grupo'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* pequenos estilos inline reaproveitados pelos modais desta tela --
   evita depender das classes .modalOverlay/.modalBox de Rotinas.module.css
   (linguagem visual diferente da usada aqui, ver topo do CSS module) */
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(15,25,40,.42)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const modalBoxStyle = { background: '#fff', borderRadius: 14, width: 440, maxWidth: '100%', padding: 20, boxShadow: '0 24px 60px rgba(0,0,0,.25)' };
const inputStyle = { width: '100%', boxSizing: 'border-box', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', font: 'inherit', background: '#fff' };
function Campo({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: .5, color: '#93a1b3', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

/* ══════════════════════════ Editor do grupo ══════════════════════════ */

function EditorGrupo({ grupoResumo, onVoltar }) {
  const [grupo, setGrupo] = useState(grupoResumo);
  const [aba, setAba]     = useState('linha');
  const [renomeando, setRenomeando] = useState(false);
  const [nomeForm, setNomeForm] = useState(grupo.nome);
  const [editandoSetor, setEditandoSetor] = useState(false);
  const [departamentos, setDepartamentos] = useState([]);
  const tipo = useMeuTipo();
  const podeDefinirSetor = tipo === 'admin' || tipo === 'gerente';

  useEffect(() => {
    if (editandoSetor && !departamentos.length) {
      listarDepartamentosNiboAPI().then(r => setDepartamentos(r.items || [])).catch(() => {});
    }
  }, [editandoSetor, departamentos.length]);

  async function salvarNome() {
    setRenomeando(false);
    if (!nomeForm.trim() || nomeForm === grupo.nome) { setNomeForm(grupo.nome); return; }
    try {
      const atualizado = await atualizarGrupo(grupo.id, { nome: nomeForm.trim() });
      setGrupo(atualizado);
    } catch {
      setNomeForm(grupo.nome);
    }
  }

  async function salvarSetor(setorId) {
    const setorEscolhido = departamentos.find(d => d.id === setorId);
    setEditandoSetor(false);
    try {
      const atualizado = await atualizarGrupo(grupo.id, { setor_nibo_id: setorId || null, setor_nome: setorEscolhido?.name || null });
      setGrupo(atualizado);
    } catch (err) {
      window.alert(`Não deu pra salvar o setor: ${err.message}`);
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.editorTopo}>
        <button type="button" className={styles.voltar} onClick={onVoltar}>
          <FiArrowLeft size={14} /> Grupos
        </button>
        {renomeando ? (
          <input
            autoFocus value={nomeForm} onChange={e => setNomeForm(e.target.value)}
            onBlur={salvarNome}
            onKeyDown={e => { if (e.key === 'Enter') salvarNome(); if (e.key === 'Escape') { setNomeForm(grupo.nome); setRenomeando(false); } }}
            style={{ ...inputStyle, width: 320, fontSize: 16, fontWeight: 700 }}
          />
        ) : (
          <h2 className={styles.editorTitulo} onDoubleClick={() => setRenomeando(true)} title="Clique duas vezes para renomear">
            {grupo.nome}
          </h2>
        )}
        {podeDefinirSetor && (
          editandoSetor ? (
            <select autoFocus defaultValue={grupo.setor_nibo_id || ''} onChange={e => salvarSetor(e.target.value)} onBlur={() => setEditandoSetor(false)} style={{ ...inputStyle, width: 200 }}>
              <option value="">Sem setor</option>
              {departamentos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          ) : (
            <button type="button" className={styles.btnSecundario} onClick={() => setEditandoSetor(true)} title="Vincular setor a este grupo">
              {grupo.setor_nome || 'Sem setor'}
            </button>
          )
        )}
      </div>

      <div className={styles.abasEditor}>
        <button type="button" className={`${styles.abaEditorBtn} ${aba === 'linha' ? styles.on : ''}`} onClick={() => setAba('linha')}>Linha do tempo</button>
        <button type="button" className={`${styles.abaEditorBtn} ${aba === 'clientes' ? styles.on : ''}`} onClick={() => setAba('clientes')}>Clientes</button>
        <button type="button" className={`${styles.abaEditorBtn} ${aba === 'execucao' ? styles.on : ''}`} onClick={() => setAba('execucao')}>Execução</button>
      </div>

      {aba === 'linha' && <AbaLinhaDoTempo grupoId={grupo.id} />}
      {aba === 'clientes' && <AbaClientes grupoId={grupo.id} />}
      {aba === 'execucao' && <AbaExecucao grupoId={grupo.id} />}
    </div>
  );
}

/* ══════════════════════════ Aba: Linha do tempo ══════════════════════════ */

function AbaLinhaDoTempo({ grupoId }) {
  const [colunas, setColunas]       = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro]             = useState('');
  const [obrigacoesNibo, setObrigacoesNibo] = useState([]);
  const [modalColunaAberto, setModalColunaAberto] = useState(false);

  const carregar = useCallback(() => {
    setCarregando(true);
    return listarColunasDoGrupo(grupoId)
      .then(r => setColunas(r.items || []))
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [grupoId]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    listarObrigacoesNibo({ top: 100 }).then(r => setObrigacoesNibo(r.items || [])).catch(() => {});
  }, []);

  async function moverColuna(coluna, delta) {
    const idx = colunas.findIndex(c => c.id === coluna.id);
    const vizinho = colunas[idx + delta];
    if (!vizinho) return;
    await Promise.all([
      atualizarColuna(coluna.id, { ordem: vizinho.ordem }),
      atualizarColuna(vizinho.id, { ordem: coluna.ordem }),
    ]);
    carregar();
  }

  async function excluirColunaConfirma(coluna) {
    if (!window.confirm(`Excluir a coluna "${coluna.nome}"? Isso remove os itens dela e todo o histórico de execução vinculado.`)) return;
    try {
      await excluirColuna(coluna.id);
      carregar();
    } catch (e) {
      setErro(e.message);
    }
  }

  async function excluirItemConfirma(item) {
    if (!window.confirm(`Excluir o item "${item.nome}"? Isso remove o histórico de execução vinculado a ele.`)) return;
    try {
      await excluirItem(item.id);
      carregar();
    } catch (e) {
      setErro(e.message);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <button type="button" className={styles.btnPrimario} onClick={() => setModalColunaAberto(true)}>
          <FiPlus size={14} /> Coluna
        </button>
      </div>

      {erro && <p className={styles.empty}>{erro}</p>}
      {carregando ? (
        <p className={styles.empty}>Carregando...</p>
      ) : colunas.length === 0 ? (
        <p className={styles.empty}>Nenhuma coluna ainda — clique em "+ Coluna" pra começar a linha do tempo.</p>
      ) : (
        colunas.map((coluna, i) => (
          <div key={coluna.id} className={styles.colunaCard}>
            <TituloColuna
              coluna={coluna}
              primeira={i === 0}
              ultima={i === colunas.length - 1}
              onMover={delta => moverColuna(coluna, delta)}
              onExcluir={() => excluirColunaConfirma(coluna)}
              onRenomeado={carregar}
              onErro={setErro}
            />

            {coluna.itens.map(item => (
              <ItemLinha
                key={item.id}
                item={item}
                obrigacoesNibo={obrigacoesNibo}
                mostrarObrigacao={coluna.origem === 'nibo'}
                onAtualizado={carregar}
                onExcluir={() => excluirItemConfirma(item)}
              />
            ))}

            <FormNovoItem colunaId={coluna.id} mostrarObrigacao={coluna.origem === 'nibo'} onCriado={carregar} />
          </div>
        ))
      )}

      {modalColunaAberto && (
        <ModalNovaColuna grupoId={grupoId} onClose={() => setModalColunaAberto(false)} onCriada={() => { setModalColunaAberto(false); carregar(); }} />
      )}
    </div>
  );
}

const MESES_CURTOS_ITEM = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/* "trimestral: jan, abr, jul, out" / "anual: mai" / "semanal" */
function textoPeriodicidade(item) {
  const m = item.mes_referencia;
  if (item.periodicidade === 'anual' && m) return `anual · ${MESES_CURTOS_ITEM[m - 1].toLowerCase()}`;
  if (item.periodicidade === 'trimestral' && m) {
    return `trimestral · ${[0, 3, 6, 9].map(d => MESES_CURTOS_ITEM[(m - 1 + d) % 12].toLowerCase()).join(', ')}`;
  }
  return item.periodicidade;
}

function ItemLinha({ item, obrigacoesNibo, mostrarObrigacao, onAtualizado, onExcluir }) {
  const [editando, setEditando] = useState(false);
  const [nome, setNome] = useState(item.nome);
  const [obrigacaoId, setObrigacaoId] = useState(item.obrigacao_nibo_id || '');
  const [obrigacaoNomeEscolhido, setObrigacaoNomeEscolhido] = useState(null);
  const [modalObrigacaoAberto, setModalObrigacaoAberto] = useState(false);
  /* dia do mês em que o item vence -- comanda a coluna na aba Vencimentos */
  const [dia, setDia] = useState(item.dia_vencimento ?? '');
  /* periodicidade: trimestral/anual só aparecem no mês escolhido (e, no
     trimestral, a cada 3 meses a partir dele) */
  const [periodicidade, setPeriodicidade] = useState(item.periodicidade || 'mensal');
  const [mesRef, setMesRef] = useState(item.mes_referencia ?? '');
  const [erroItem, setErroItem] = useState('');
  const precisaMes = periodicidade === 'trimestral' || periodicidade === 'anual';

  async function salvar() {
    const mudouNome = nome.trim() && nome !== item.nome;
    const mudouObrigacao = mostrarObrigacao && obrigacaoId !== (item.obrigacao_nibo_id || '');
    const mudouDia = String(dia) !== String(item.dia_vencimento ?? '');
    const mudouPeriodo = periodicidade !== (item.periodicidade || 'mensal')
      || (precisaMes && String(mesRef) !== String(item.mes_referencia ?? ''));
    if (precisaMes && !mesRef) { setErroItem('Escolha o mês em que vence'); return; }
    setEditando(false); setErroItem('');
    if (!mudouNome && !mudouObrigacao && !mudouDia && !mudouPeriodo) return;
    try {
      await atualizarItem(item.id, {
        ...(mudouNome ? { nome: nome.trim() } : {}),
        ...(mudouObrigacao ? { obrigacao_nibo_id: obrigacaoId || null } : {}),
        ...(mudouDia ? { dia_vencimento: dia === '' ? null : Number(dia) } : {}),
        ...(mudouPeriodo ? { periodicidade, mes_referencia: precisaMes ? Number(mesRef) : null } : {}),
      });
      onAtualizado();
    } catch { /* ignora -- lista recarrega no próximo fetch manual */ }
  }

  const obrigacaoNome = obrigacaoNomeEscolhido
    || obrigacoesNibo.find(o => o.id === item.obrigacao_nibo_id)?.description
    || obrigacoesNibo.find(o => o.id === item.obrigacao_nibo_id)?.nickname;

  if (editando) {
    return (
      <div className={styles.itemLinha}>
        <input value={nome} onChange={e => setNome(e.target.value)} style={{ ...inputStyle, flex: 1, padding: '4px 8px' }} autoFocus />
        {mostrarObrigacao && (
          <button type="button" className={styles.botaoObrigacao} onClick={() => setModalObrigacaoAberto(true)}>
            {obrigacaoNome || 'Selecionar obrigação da Nibo'}
          </button>
        )}
        <label className={styles.itemDia}
          title={mostrarObrigacao ? 'Em branco = usa o vencimento da Nibo' : 'Em branco = usa o vencimento da próxima obrigação da linha do tempo'}>
          vence dia
          <input type="number" min={1} max={31} value={dia} placeholder="—"
            onChange={e => setDia(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') salvar(); }} />
        </label>
        <select className={styles.itemPeriodo} value={periodicidade} onChange={e => setPeriodicidade(e.target.value)} title="Periodicidade">
          <option value="semanal">Semanal</option>
          <option value="mensal">Mensal</option>
          <option value="trimestral">Trimestral</option>
          <option value="anual">Anual</option>
        </select>
        {precisaMes && (
          <select className={styles.itemPeriodo} value={mesRef} onChange={e => setMesRef(e.target.value)}
            title={periodicidade === 'trimestral' ? 'Primeiro mês em que vence (repete a cada 3 meses)' : 'Mês em que vence'}>
            <option value="">{periodicidade === 'trimestral' ? '1º mês...' : 'mês...'}</option>
            {MESES_CURTOS_ITEM.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        )}
        {erroItem && <span className={styles.itemErro}>{erroItem}</span>}
        <button type="button" className={styles.btnFantasma} onClick={salvar}><FiCheck size={13} /></button>
        {modalObrigacaoAberto && (
          <ModalEscolherObrigacaoNibo
            valorAtual={obrigacaoId}
            onClose={() => setModalObrigacaoAberto(false)}
            onEscolher={o => { setObrigacaoId(o.id); setObrigacaoNomeEscolhido(o.description || o.nickname); setModalObrigacaoAberto(false); }}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.itemLinha}>
      <span>{item.nome}</span>
      {mostrarObrigacao && <span className={styles.itemObrigacao}>{obrigacaoNome || 'sem obrigação vinculada'}</span>}
      {item.dia_vencimento && <span className={styles.itemDiaSelo} title="Dia de vencimento (aba Vencimentos)">dia {String(item.dia_vencimento).padStart(2, '0')}</span>}
      {item.periodicidade && item.periodicidade !== 'mensal' && (
        <span className={styles.itemPeriodoSelo}>{textoPeriodicidade(item)}</span>
      )}
      <div className={styles.itemAcoes}>
        <button type="button" className={styles.btnFantasma} title="Editar" onClick={() => setEditando(true)}><FiEdit2 size={12} /></button>
        <button type="button" className={styles.btnFantasma} title="Excluir" onClick={onExcluir}><FiTrash2 size={12} /></button>
      </div>
    </div>
  );
}

/* Título da coluna com edição no lugar -- mesmo comportamento que os itens
   dela já tinham (lápis -> input -> confirma), que faltava aqui. */
function TituloColuna({ coluna, primeira, ultima, onMover, onExcluir, onRenomeado, onErro }) {
  const [editando, setEditando] = useState(false);
  const [nome, setNome] = useState(coluna.nome);

  function cancelar() {
    setNome(coluna.nome);
    setEditando(false);
  }

  async function salvar() {
    const limpo = nome.trim();
    if (!limpo || limpo === coluna.nome) return cancelar();
    try {
      await atualizarColuna(coluna.id, { nome: limpo });
      setEditando(false);
      onRenomeado();
    } catch (e) {
      onErro(e.message);
      cancelar();
    }
  }

  if (editando) {
    return (
      <div className={styles.colunaHeader}>
        <input
          value={nome}
          onChange={e => setNome(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') salvar();
            if (e.key === 'Escape') cancelar();
          }}
          style={{ ...inputStyle, flex: 1, padding: '4px 8px', fontWeight: 700 }}
          autoFocus
        />
        <button type="button" className={styles.btnFantasma} title="Salvar" onClick={salvar}><FiCheck size={13} /></button>
        <button type="button" className={styles.btnFantasma} title="Cancelar" onClick={cancelar}><FiX size={13} /></button>
      </div>
    );
  }

  return (
    <div className={styles.colunaHeader}>
      <span className={styles.colunaNome}>{coluna.nome}</span>
      {coluna.origem === 'nibo' && <span className={styles.badgeNibo}>NIBO</span>}
      <div className={styles.colunaAcoes}>
        <button type="button" className={styles.btnFantasma} title="Renomear coluna" onClick={() => setEditando(true)}><FiEdit2 size={13} /></button>
        <button type="button" className={styles.btnFantasma} title="Mover pra cima" disabled={primeira} onClick={() => onMover(-1)}><FiArrowUp size={13} /></button>
        <button type="button" className={styles.btnFantasma} title="Mover pra baixo" disabled={ultima} onClick={() => onMover(1)}><FiArrowDown size={13} /></button>
        <button type="button" className={styles.btnFantasma} title="Excluir coluna" onClick={onExcluir}><FiTrash2 size={13} /></button>
      </div>
    </div>
  );
}

function FormNovoItem({ colunaId, mostrarObrigacao, onCriado }) {
  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState('');
  const [obrigacaoId, setObrigacaoId] = useState('');
  const [obrigacaoNome, setObrigacaoNome] = useState('');
  const [modalObrigacaoAberto, setModalObrigacaoAberto] = useState(false);

  async function salvar() {
    if (!nome.trim()) return;
    await criarItem(colunaId, { nome: nome.trim(), obrigacao_nibo_id: obrigacaoId || null });
    setNome(''); setObrigacaoId(''); setObrigacaoNome(''); setAberto(false);
    onCriado();
  }

  if (!aberto) {
    return (
      <button type="button" className={styles.btnSecundario} style={{ marginTop: 8, marginLeft: 20 }} onClick={() => setAberto(true)}>
        <FiPlus size={12} /> Item
      </button>
    );
  }
  return (
    <div className={styles.itemLinha} style={{ borderTop: '1px solid #eef2f7', marginTop: 4 }}>
      <input autoFocus value={nome} onChange={e => setNome(e.target.value)} placeholder="Nome do item" style={{ ...inputStyle, flex: 1, padding: '4px 8px' }}
        onKeyDown={e => { if (e.key === 'Enter') salvar(); if (e.key === 'Escape') setAberto(false); }} />
      {mostrarObrigacao && (
        <button type="button" className={styles.botaoObrigacao} onClick={() => setModalObrigacaoAberto(true)}>
          {obrigacaoNome || 'Selecionar obrigação da Nibo'}
        </button>
      )}
      <button type="button" className={styles.btnFantasma} onClick={salvar}><FiCheck size={13} /></button>
      <button type="button" className={styles.btnFantasma} onClick={() => setAberto(false)}><FiX size={13} /></button>
      {modalObrigacaoAberto && (
        <ModalEscolherObrigacaoNibo
          valorAtual={obrigacaoId}
          onClose={() => setModalObrigacaoAberto(false)}
          onEscolher={o => { setObrigacaoId(o.id); setObrigacaoNome(o.description || o.nickname); setModalObrigacaoAberto(false); }}
        />
      )}
    </div>
  );
}

/* ── seletor de obrigação da Nibo -- aberto a partir do item (criação ou
   edição), nunca mais da grade de execução: o vínculo é uma propriedade do
   ITEM (vale pra todo cliente que tiver essa obrigação), não uma coisa que
   se resolve cliente a cliente na tela principal. ── */
function ModalEscolherObrigacaoNibo({ valorAtual, onEscolher, onClose }) {
  const [obrigacoes, setObrigacoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  useFecharComEsc(onClose);

  useEffect(() => {
    listarObrigacoesNibo({ top: 300 }).then(r => setObrigacoes(r.items || [])).catch(() => {}).finally(() => setCarregando(false));
  }, []);

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return obrigacoes.filter(o => !q
      || (o.description || '').toLowerCase().includes(q)
      || (o.nickname || '').toLowerCase().includes(q));
  }, [obrigacoes, busca]);

  return (
    <div style={{ ...overlayStyle, zIndex: 9500 }}>
      <div style={{ ...modalBoxStyle, width: 420 }} onClick={e => e.stopPropagation()} className={styles.wrap}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Selecionar obrigação da Nibo</h3>
        <input autoFocus value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar obrigação..." style={inputStyle} />
        <div style={{ maxHeight: 320, overflowY: 'auto', marginTop: 10, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {carregando ? (
            <p className={styles.empty}>Carregando...</p>
          ) : filtradas.length === 0 ? (
            <p className={styles.empty}>Nenhuma obrigação encontrada.</p>
          ) : filtradas.map(o => (
            <button
              key={o.id}
              type="button"
              onClick={() => onEscolher(o)}
              style={{
                display: 'flex', alignItems: 'baseline', gap: 8,
                textAlign: 'left', padding: '7px 10px', borderRadius: 7, border: 'none', cursor: 'pointer',
                background: o.id === valorAtual ? '#dce8fd' : 'transparent', fontSize: 13, color: '#16202e', fontFamily: 'inherit',
              }}
            >
              <span>{o.description || o.nickname}</span>
              {o.description && o.nickname && <span style={{ fontSize: 10.5, color: '#93a1b3', flexShrink: 0 }}>{o.nickname}</span>}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
          <button type="button" className={styles.btnSecundario} onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

function ModalNovaColuna({ grupoId, onClose, onCriada }) {
  const [nome, setNome] = useState('');
  const [origem, setOrigem] = useState('interna');
  const [itensTexto, setItensTexto] = useState('');
  const [escopo, setEscopo] = useState('todos');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  useFecharComEsc(onClose);

  async function salvar(e) {
    e.preventDefault();
    if (!nome.trim()) return;
    setSalvando(true); setErro('');
    try {
      await criarColuna({
        grupo_id: grupoId, nome: nome.trim(), origem,
        itens: itensTexto.split('\n').map(s => s.trim()).filter(Boolean),
        escopo,
      });
      onCriada();
    } catch (err) {
      setErro(err.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div style={overlayStyle}>
      <form style={modalBoxStyle} onClick={e => e.stopPropagation()} onSubmit={salvar} className={styles.wrap}>
        <h3 style={{ margin: '0 0 14px', fontSize: 16 }}>Nova coluna</h3>
        <Campo label="Nome do marco">
          <input autoFocus value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex.: FGTS Digital" style={inputStyle} />
        </Campo>
        <Campo label="Origem">
          <select value={origem} onChange={e => setOrigem(e.target.value)} style={inputStyle}>
            <option value="interna">Interna — controlada pela equipe</option>
            <option value="nibo">Vinculada — obrigação vinda da Nibo</option>
          </select>
        </Campo>
        <Campo label="Itens (um por linha)">
          <textarea value={itensTexto} onChange={e => setItensTexto(e.target.value)} rows={3} placeholder={'Guia\nRecibo'} style={{ ...inputStyle, resize: 'vertical' }} />
        </Campo>
        <Campo label="Aplicar a">
          <select value={escopo} onChange={e => setEscopo(e.target.value)} style={inputStyle}>
            <option value="todos">Todos os clientes já vinculados ao grupo</option>
            <option value="nenhum">Nenhum — habilitar cliente a cliente depois</option>
          </select>
        </Campo>
        {erro && <p style={{ color: '#d0453b', fontSize: 12 }}>{erro}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className={styles.btnSecundario} onClick={onClose}>Cancelar</button>
          <button type="submit" className={styles.btnPrimario} disabled={salvando || !nome.trim()}>{salvando ? 'Criando...' : 'Criar coluna'}</button>
        </div>
      </form>
    </div>
  );
}

/* ══════════════════════════ Aba: Clientes ══════════════════════════ */

function AbaClientes({ grupoId }) {
  const [vinculados, setVinculados]     = useState([]);
  const [todos, setTodos]               = useState([]);
  const [carregando, setCarregando]     = useState(true);
  const [erro, setErro]                 = useState('');
  const [busca, setBusca]               = useState('');
  const [selecionados, setSelecionados] = useState(() => new Set());

  const carregar = useCallback(() => {
    setCarregando(true);
    return Promise.all([listarClientesDoGrupo(grupoId), listarNossosClientes()])
      .then(([v, t]) => { setVinculados(v.items || []); setTodos(t || []); })
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [grupoId]);
  useEffect(() => { carregar(); }, [carregar]);

  const idsVinculados = useMemo(() => new Set(vinculados.map(c => c.id)), [vinculados]);
  const disponiveis = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return todos
      .filter(c => !idsVinculados.has(c.id))
      .filter(c => !q || c.nome.toLowerCase().includes(q) || (c.codigo || '').includes(q))
      .sort((a, b) => (a.codigo || '').localeCompare(b.codigo || '', 'pt-BR', { numeric: true }));
  }, [todos, idsVinculados, busca]);

  function alternarSelecionado(id) {
    setSelecionados(prev => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id); else novo.add(id);
      return novo;
    });
  }

  async function vincularSelecionados() {
    if (!selecionados.size) return;
    await vincularClientesAoGrupo(grupoId, [...selecionados]);
    setSelecionados(new Set());
    carregar();
  }

  async function desvincular(clienteId) {
    await desvincularClienteDoGrupo(grupoId, clienteId);
    carregar();
  }

  if (carregando) return <p className={styles.empty}>Carregando...</p>;
  if (erro) return <p className={styles.empty}>{erro}</p>;

  return (
    <div>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px', minWidth: 280 }}>
          <h4 style={{ fontSize: 13, margin: '0 0 8px' }}>Vinculados ({vinculados.length})</h4>
          {vinculados.length === 0 ? (
            <p className={styles.empty}>Nenhum cliente vinculado ainda.</p>
          ) : (
            <div className={styles.listaClientesGrid} style={{ gridTemplateColumns: '1fr' }}>
              {vinculados.map(c => (
                <div key={c.id} className={styles.clienteChk} style={{ justifyContent: 'space-between' }}>
                  <span><strong>{c.codigo}</strong> — {c.nome} {!c.nibo_customer_id && <span style={{ color: '#a35a08' }}>(sem vínculo Nibo)</span>}</span>
                  <button type="button" className={styles.btnFantasma} title="Desvincular" onClick={() => desvincular(c.id)}><FiX size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ flex: '1 1 320px', minWidth: 280 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <h4 style={{ fontSize: 13, margin: 0 }}>Adicionar clientes</h4>
            {selecionados.size > 0 && (
              <button type="button" className={styles.btnPrimario} style={{ padding: '4px 10px', fontSize: 12 }} onClick={vincularSelecionados}>
                Vincular {selecionados.size}
              </button>
            )}
          </div>
          <input className={styles.buscaCliente} placeholder="Buscar código ou nome..." value={busca} onChange={e => setBusca(e.target.value)} />
          {disponiveis.length > 0 && (
            <button type="button" className={styles.btnSecundario} style={{ marginTop: 8 }}
              onClick={() => setSelecionados(new Set(disponiveis.map(c => c.id)))}>
              Selecionar todos os filtrados ({disponiveis.length})
            </button>
          )}
          <div className={styles.listaClientesGrid} style={{ gridTemplateColumns: '1fr', maxHeight: 360, overflowY: 'auto', marginTop: 8 }}>
            {disponiveis.map(c => (
              <label key={c.id} className={styles.clienteChk}>
                <input type="checkbox" checked={selecionados.has(c.id)} onChange={() => alternarSelecionado(c.id)} />
                <strong>{c.codigo}</strong> — {c.nome}
              </label>
            ))}
            {disponiveis.length === 0 && <p className={styles.empty}>Nenhum cliente encontrado.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════ Aba: Execução ══════════════════════════ */

/* item de coluna 'nibo' cuja obrigação é trimestral/anual: só conta nos
   meses em que ela realmente vence na Nibo (existeNaCompetencia) -- fora
   disso é como se o item não existisse pra essa competência, igual coluna
   que não se aplica ao cliente. Coluna 'interna' nunca entra aqui, sempre
   mensal (o time controla o ritmo dela). */
function itemVenceNestaCompetencia(coluna, itemEstado) {
  /* o backend já junta as duas regras em existeNaCompetencia: a
     periodicidade do item (trimestral/anual, vale pra qualquer coluna) e,
     em coluna 'nibo', se existe protocolo no mês */
  return itemEstado?.existeNaCompetencia !== false;
}

function analisarCliente(estrutura, cliente) {
  let total = 0, done = 0, aguardando = 0;
  const colunaEstado = new Map(cliente.colunas.map(c => [c.colunaId, c]));
  estrutura.forEach(coluna => {
    const cc = colunaEstado.get(coluna.id);
    if (cc && !cc.aplica) return;
    const itensEstado = new Map((cc?.itens || []).map(it => [it.itemId, it]));
    coluna.itens.forEach(item => {
      const it = itensEstado.get(item.id);
      /* uma marcação manual já feita conta sempre, mesmo se a Nibo não
         confirmar mais que a obrigação vence nesta competência (ver
         montarTrilha logo abaixo) */
      if (!itemVenceNestaCompetencia(coluna, it) && !it?.marcado) return;
      total += 1;
      const verde = coluna.origem === 'nibo' ? it?.confirmado : it?.marcado;
      if (verde) done += 1;
      if (coluna.origem === 'nibo' && it?.marcado && !it?.confirmado) aguardando += 1;
    });
  });
  const tk = cliente.tarefas || { total: 0, concluidas: 0 };
  total += tk.total; done += tk.concluidas;
  return { total, done, aguardando, tk, fechado: total > 0 && done === total };
}

function montarTrilha(estrutura, cliente) {
  const cells = [];
  let gate = false;
  const colunaEstado = new Map(cliente.colunas.map(c => [c.colunaId, c]));
  estrutura.forEach(coluna => {
    const cc = colunaEstado.get(coluna.id) || { aplica: true, itens: [] };
    const itensEstado = new Map((cc.itens || []).map(it => [it.itemId, it]));
    coluna.itens.forEach((item, k) => {
      const est = itensEstado.get(item.id) || { marcado: false, confirmado: false, existeNaCompetencia: true };
      const venceNoMes = itemVenceNestaCompetencia(coluna, est);
      /* uma bolinha já marcada manualmente nunca pode virar "não aplicável"
         sozinha -- sem esse "|| est.marcado" ela sumia (voltava pra um
         estado que parecia "aberto") toda vez que a checagem ao vivo com a
         Nibo deixava de achar o protocolo daquela competência (prazo
         interno mudou, obrigação foi desativada depois, falha parcial da
         API etc.), mesmo com a marcação intacta no banco. */
      const viva = cc.aplica && (venceNoMes || est.marcado);
      const conf = viva && coluna.origem === 'nibo' && !!est.confirmado;
      const ok = viva && (!!est.marcado || conf);
      if (cc.aplica && venceNoMes) gate = ok;
      cells.push({
        tipo: 'item', coluna, item, k, gate, done: ok, conf, marcado: !!est.marcado,
        aplica: viva, primeira: k === 0,
      });
    });
  });
  const tk = cliente.tarefas || { total: 0, concluidas: 0 };
  const tkOk = tk.total === 0 || tk.concluidas === tk.total;
  gate = gate && tkOk;
  cells.push({ tipo: 'tarefas', tk, gate, primeira: true });
  return cells;
}

export function AbaExecucao({ grupoId, mostrarSeletorGrupo = true }) {
  const navigate = useNavigate();
  const tipo = useMeuTipo();
  const podeVerTodosSetores = tipo === 'admin' || tipo === 'gerente';
  const [competencia, setCompetencia] = useState(() => competenciaLembrada(grupoId));
  const [dados, setDados]             = useState(null);
  const [carregando, setCarregando]   = useState(true);
  const [erro, setErro]               = useState('');
  const [busca, setBusca]             = useState('');
  const [mostrarFinalizados, setMostrarFinalizados] = useState(false);
  const [popoverAberto, setPopoverAberto] = useState(null); // clienteId
  const [historicoAberto, setHistoricoAberto] = useState(null); // cliente
  /* o ícone acende/apaga na hora em que o modal salva, sem esperar o
     próximo carregamento da execução: clienteId -> tem anotação? */
  const [historicoLocal, setHistoricoLocal] = useState(() => new Map());
  const [larguraCliente, setLarguraCliente] = useState(larguraClienteLembrada);
  const [grupos, setGrupos]           = useState([]);
  const [setorFiltro, setSetorFiltro] = useState('todos');
  const [grupoAtivoId, setGrupoAtivoId] = useState(grupoId);

  /* troca o grupo exibido quando o pai passa outro grupoId (ex.: aba
     Auditoria trocando de aba do topo) -- mas depois disso o filtro por
     grupo aqui dentro manda, o usuário pode navegar livre entre os grupos
     que ele enxerga sem precisar sair da tela. */
  useEffect(() => {
    setGrupoAtivoId(grupoId);
    setCompetencia(competenciaLembrada(grupoId));
  }, [grupoId]);

  /* grupo e competência trocam juntos -- cada grupo abre na competência
     que foi usada nele por último (ver competenciaLembrada) */
  function trocarGrupo(id) {
    setGrupoAtivoId(id);
    setCompetencia(competenciaLembrada(id));
  }

  function mudarCompetencia(nova) {
    setCompetencia(nova);
    lembrarCompetencia(grupoAtivoId, nova);
  }

  /* lista de grupos já vem filtrada por setor pelo backend -- um usuário
     comum só recebe os grupos do próprio setor (ou sem setor definido);
     admin/gerente recebem todos e ainda podem filtrar por setor aqui. */
  useEffect(() => {
    listarGrupos().then(r => setGrupos((r.items || []).filter(g => g.ativo))).catch(() => {});
  }, []);

  const setoresDisponiveis = useMemo(
    () => [...new Set(grupos.filter(g => g.setor_nome).map(g => g.setor_nome))],
    [grupos]
  );
  const gruposFiltrados = useMemo(
    () => setorFiltro === 'todos' ? grupos : grupos.filter(g => g.setor_nome === setorFiltro),
    [grupos, setorFiltro]
  );

  const carregar = useCallback(() => {
    if (!grupoAtivoId) return undefined;
    setCarregando(true); setErro('');
    return buscarExecucao(grupoAtivoId, competencia)
      .then(setDados)
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [grupoAtivoId, competencia]);
  useEffect(() => { carregar(); }, [carregar]);

  const estrutura = useMemo(() => dados?.estrutura || [], [dados]);
  const totalColunasItens = estrutura.reduce((a, c) => a + c.itens.length, 0);

  const analisePorCliente = useMemo(() => {
    const mapa = new Map();
    (dados?.clientes || []).forEach(c => mapa.set(c.id, analisarCliente(estrutura, c)));
    return mapa;
  }, [dados, estrutura]);

  const listaFiltrada = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return (dados?.clientes || []).filter(c => {
      const analise = analisePorCliente.get(c.id);
      if (mostrarFinalizados ? !analise.fechado : analise.fechado) return false;
      if (q && !(c.nome.toLowerCase().includes(q) || (c.codigo || '').includes(q))) return false;
      return true;
    });
  }, [dados, analisePorCliente, busca, mostrarFinalizados]);

  const totalFinalizados = (dados?.clientes || []).filter(c => analisePorCliente.get(c.id)?.fechado).length;

  /* marca/desmarca a bolinha localmente (otimista) em vez de rebuscar a
     grade inteira -- buscarExecucao faz várias queries + uma chamada ao
     vivo pra Nibo, então recarregar tudo a cada clique deixava a marcação
     lenta e piscava a tela inteira em "Carregando...". Aqui só troca o
     item clicado no estado já carregado; se o PATCH falhar, desfaz. */
  function aplicarMarcadoLocal(clienteId, itemId, marcado) {
    setDados(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        clientes: prev.clientes.map(c => c.id !== clienteId ? c : {
          ...c,
          colunas: c.colunas.map(col => ({
            ...col,
            itens: col.itens.map(it => it.itemId === itemId ? { ...it, marcado } : it),
          })),
        }),
      };
    });
  }

  async function alternarMarcado(cliente, item) {
    const jaMarcado = cliente.colunas.find(c => c.itens.some(it => it.itemId === item.id))?.itens.find(it => it.itemId === item.id)?.marcado;
    const novoMarcado = !jaMarcado;
    aplicarMarcadoLocal(cliente.id, item.id, novoMarcado);
    try {
      await marcarExecucao(grupoAtivoId, { cliente_id: cliente.id, competencia, item_id: item.id, marcado: novoMarcado });
    } catch (e) {
      setErro(e.message);
      aplicarMarcadoLocal(cliente.id, item.id, jaMarcado);
    }
  }

  async function alternarAplicaColuna(clienteId, colunaId, aplica) {
    await definirVinculoClienteColuna(colunaId, clienteId, { aplica });
    carregar();
  }

  /* arrasta a divisão entre a coluna Cliente e a primeira obrigação --
     só grava no localStorage quando solta o mouse (não a cada pixel). */
  function iniciarResizeCliente(e) {
    e.preventDefault();
    const inicioX = e.clientX;
    const larguraInicial = larguraCliente;
    function aoMover(ev) {
      const nova = larguraInicial + (ev.clientX - inicioX);
      setLarguraCliente(Math.min(LARGURA_CLIENTE_MAX, Math.max(LARGURA_CLIENTE_MIN, nova)));
    }
    function aoSoltar() {
      window.removeEventListener('mousemove', aoMover);
      window.removeEventListener('mouseup', aoSoltar);
      setLarguraCliente(atual => { lembrarLarguraCliente(atual); return atual; });
    }
    window.addEventListener('mousemove', aoMover);
    window.addEventListener('mouseup', aoSoltar);
  }

  /* colunas de item se ajustam ao conteúdo (max-content) -- cada uma fica
     só do tamanho do nome mais comprido que tiver que caber ali (nome do
     item no cabeçalho, já que a bolinha é minúscula perto disso). Nunca
     quebra texto (white-space: nowrap no CSS); se a soma passar da largura
     da tela, .gridScroll rola pro lado. Cabeçalho (2 linhas) e todas as
     linhas de cliente são células de UM ÚNICO grid (não dois grids
     separados como antes) -- é isso que garante que as colunas do
     cabeçalho e do corpo fiquem exatamente alinhadas mesmo com largura
     variável por coluna; com dois grids independentes, cada um resolvia
     "max-content" com base só no próprio conteúdo (cabeçalho teria uma
     largura, célula do corpo outra) e nada alinhava. */
  const gridTemplateColumns = `${larguraCliente}px repeat(${totalColunasItens}, max-content) max-content`;

  /* posição explícita de cada célula do cabeçalho e de cada célula do
     corpo (linha 1-2 = cabeçalho, linha 3+ = um cliente por linha) --
     não confia em auto-placement, que bagunçava o alinhamento quando o
     número de itens por coluna variava. */
  let colCursor = 2; // coluna 1 = Cliente
  const gruposHeader = estrutura.map(coluna => {
    const colStart = colCursor;
    colCursor += coluna.itens.length;
    return { coluna, colStart, colEnd: colCursor };
  });
  const colTarefas = colCursor;
  const itensHeader = estrutura.flatMap(coluna => coluna.itens.map(item => ({ item, coluna, col: gruposHeader.find(g => g.coluna.id === coluna.id).colStart + coluna.itens.indexOf(item) })));

  return (
    <div className={styles.wrap} style={{ '--rowh': '46px', '--dot': '13px' }}>
      <div className={styles.tools}>
        <div className={styles.toolsGrupo}>
          <div className={styles.comp}>
            <button type="button" onClick={() => mudarCompetencia(deslocarCompetencia(competencia, -1))}><FiChevronLeft size={14} /></button>
            <b>{rotuloCompetencia(competencia)}</b>
            <button type="button" onClick={() => mudarCompetencia(deslocarCompetencia(competencia, 1))}><FiChevronRight size={14} /></button>
          </div>
          {competencia !== competenciaAtual() && (
            <button type="button" className={styles.btnSecundario} onClick={() => mudarCompetencia(competenciaAtual())} title="Voltar pro mês corrente">
              Ir para {rotuloCompetencia(competenciaAtual())}
            </button>
          )}
        </div>

        <div className={styles.toolsFiltros}>
          <input type="search" placeholder="Buscar código ou cliente..." value={busca} onChange={e => setBusca(e.target.value)} />
          {podeVerTodosSetores && setoresDisponiveis.length > 0 && (
            <select value={setorFiltro} onChange={e => setSetorFiltro(e.target.value)}>
              <option value="todos">Todos os setores</option>
              {setoresDisponiveis.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {mostrarSeletorGrupo && (
            <select value={grupoAtivoId || ''} onChange={e => trocarGrupo(e.target.value)}>
              {gruposFiltrados.length === 0 && <option value="">Nenhum grupo</option>}
              {gruposFiltrados.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
            </select>
          )}
        </div>

        {totalFinalizados > 0 && (
          <button type="button" className={`${styles.btnSecundario} ${styles.toolsAcaoDireita}`} onClick={() => setMostrarFinalizados(v => !v)}>
            {mostrarFinalizados ? 'Voltar aos pendentes' : `Mostrar finalizados (${totalFinalizados})`}
          </button>
        )}
      </div>

      {erro && <p className={styles.empty}>{erro}</p>}

      {carregando ? (
        <p className={styles.empty}>Carregando...</p>
      ) : estrutura.length === 0 ? (
        <p className={styles.empty}>Esse grupo ainda não tem colunas — configure a linha do tempo primeiro.</p>
      ) : listaFiltrada.length === 0 ? (
        <p className={styles.empty}>{mostrarFinalizados ? 'Nenhum cliente finalizado nesta competência.' : 'Tudo concluído nesta competência.'}</p>
      ) : (
        <div className={styles.gridScroll}>
          <div className={styles.grid} style={{ gridTemplateColumns }}>
            <div className={styles.hCliTop} style={{ gridRow: 1, gridColumn: 1 }}>Rotinas</div>
            <div className={styles.hCliBottom} style={{ gridRow: 2, gridColumn: 1 }} />
            <div
              className={styles.resizeCliente}
              style={{ left: larguraCliente - 3 }}
              onMouseDown={iniciarResizeCliente}
              title="Arraste pra ajustar a largura da coluna Cliente"
            />
            {gruposHeader.map(({ coluna, colStart, colEnd }) => (
              <div key={coluna.id} className={styles.hGrp} style={{ gridRow: 1, gridColumn: `${colStart} / ${colEnd}` }}>
                <span>{coluna.nome}</span>
                {coluna.origem === 'nibo' && <span className={styles.badgeNibo}>NIBO</span>}
              </div>
            ))}
            <div className={styles.hGrp} style={{ gridRow: 1, gridColumn: colTarefas }}><span>Tarefas</span><span className={styles.tsk}>ABA</span></div>
            {itensHeader.map(({ item, col }) => (
              <div key={item.id} className={styles.hEtp} style={{ gridRow: 2, gridColumn: col }}>
                <span>{item.nome}</span>
                <span className={styles.hEtpData}>{item.vencimento ? rotuloVencimento(item.vencimento) : ' '}</span>
              </div>
            ))}
            <div className={styles.hEtp} style={{ gridRow: 2, gridColumn: colTarefas }}>
              <span>conclusão</span>
              <span className={styles.hEtpData}> </span>
            </div>

            {listaFiltrada.map((cliente, i) => {
              const trilha = montarTrilha(estrutura, cliente);
              const analise = analisePorCliente.get(cliente.id);
              const last = trilha.length - 1;
              const linha = i + 3; // linhas 1-2 são o cabeçalho
              const fundo = analise.fechado ? '#e6f6ee' : (i % 2 === 1 ? '#fafcff' : '#fff');
              const temHistorico = historicoLocal.has(cliente.id)
                ? historicoLocal.get(cliente.id)
                : !!cliente.temHistorico;
              return (
                <React.Fragment key={cliente.id}>
                  <div className={styles.cli} style={{ gridRow: linha, gridColumn: 1, background: fundo }}>
                    <div className={styles.cliIn}>
                      <div className={styles.l1}>
                        <button
                          type="button"
                          className={`${styles.hist} ${temHistorico ? styles.histOn : ''}`}
                          title={temHistorico ? 'Histórico do cliente — há anotações' : 'Histórico do cliente — sem anotações'}
                          onClick={() => setHistoricoAberto(cliente)}
                        >
                          <FiFileText size={14} />
                        </button>
                        <span className={styles.cod}>{cliente.codigo}</span>
                        <span className={styles.nomeCli}>{cliente.nome}</span>
                        {analise.fechado && <span className={styles.flag}>●</span>}
                        {analise.aguardando > 0 && <span className={styles.alert} title={`${analise.aguardando} item(ns) aguardando a Nibo`}>●</span>}
                      </div>
                    </div>
                    <button type="button" className={styles.cfg} title="Marcos deste cliente" onClick={() => setPopoverAberto(popoverAberto === cliente.id ? null : cliente.id)}>
                      <FiMoreHorizontal size={15} />
                    </button>
                    {popoverAberto === cliente.id && (
                      <PopoverMarcos
                        cliente={cliente}
                        estrutura={estrutura}
                        onFechar={() => setPopoverAberto(null)}
                        onAlternar={(colunaId, aplica) => alternarAplicaColuna(cliente.id, colunaId, aplica)}
                      />
                    )}
                  </div>

                  {trilha.map((x, idx) => {
                    const prev = trilha[idx - 1];
                    const col = idx === last ? colTarefas : idx + 2;
                    const cls = [styles.cell, idx === 0 ? styles.start : ''].join(' ');
                    const estiloCelula = { gridRow: linha, gridColumn: col, background: fundo };
                    if (x.tipo === 'tarefas') {
                      const ok = x.tk.total === 0 || x.tk.concluidas === x.tk.total;
                      const pend = x.tk.total - x.tk.concluidas;
                      return (
                        <div key={`tarefas-${cliente.id}`} className={cls} style={estiloCelula}>
                          <span className={`${styles.ln} ${styles.l} ${prev?.gate ? styles.on : ''}`} />
                          {idx !== last && <span className={`${styles.ln} ${styles.r} ${x.gate ? styles.on : ''}`} />}
                          <button
                            type="button"
                            className={`${styles.dot} ${styles.task} ${ok ? styles.on : ''}`}
                            title={x.tk.total ? `Tarefas: ${x.tk.concluidas} de ${x.tk.total} — abrir aba Tarefas` : 'Sem tarefas nesta competência'}
                            onClick={() => navigate('/app/rotinas?aba=tarefas')}
                          >
                            {!ok && pend > 0 && <span className={styles.num}>{pend}</span>}
                          </button>
                        </div>
                      );
                    }
                    if (!x.aplica) {
                      return (
                        <div key={item_key(x)} className={cls} style={estiloCelula}>
                          <span className={`${styles.ln} ${styles.l} ${prev?.gate ? styles.on : ''}`} />
                          {idx !== last && <span className={`${styles.ln} ${styles.r} ${x.gate ? styles.on : ''}`} />}
                          <span className={`${styles.dot} ${styles.na}`} />
                        </div>
                      );
                    }
                    const estado = x.conf ? styles.on : (x.marcado ? (x.coluna.origem === 'nibo' ? styles.wait : styles.on) : '');
                    return (
                      <div key={item_key(x)} className={cls} style={estiloCelula}>
                        <span className={`${styles.ln} ${styles.l} ${prev?.gate ? styles.on : ''}`} />
                        {idx !== last && <span className={`${styles.ln} ${styles.r} ${x.gate ? styles.on : ''}`} />}
                        <button
                          type="button"
                          className={`${styles.dot} ${estado}`}
                          title={x.coluna.origem === 'nibo'
                            ? `${x.coluna.nome} · ${x.item.nome} — ${x.conf ? 'confirmado pela Nibo' : (x.marcado ? 'feito, aguardando confirmação da Nibo' : 'pendente')}`
                            : `${x.coluna.nome} · ${x.item.nome}`}
                          onClick={() => alternarMarcado(cliente, x.item)}
                        />
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      <div className={styles.footerBar}>
        <span>{listaFiltrada.length} {mostrarFinalizados ? 'finalizados' : 'em execução'} · {(dados?.clientes || []).length} no grupo</span>
        <span className={styles.grow} />
        <span>Clientes 100% concluídos saem da lista da competência</span>
      </div>

      {historicoAberto && (
        <ModalHistoricoCliente
          cliente={historicoAberto}
          onFechar={() => setHistoricoAberto(null)}
          onSalvo={tem => setHistoricoLocal(m => new Map(m).set(historicoAberto.id, tem))}
        />
      )}
    </div>
  );
}

function item_key(x) { return `${x.coluna.id}-${x.item.id}`; }

function PopoverMarcos({ cliente, estrutura, onFechar, onAlternar }) {
  useEffect(() => {
    function aoClicarFora() { onFechar(); }
    document.addEventListener('click', aoClicarFora);
    return () => document.removeEventListener('click', aoClicarFora);
  }, [onFechar]);

  const colunaEstado = new Map(cliente.colunas.map(c => [c.colunaId, c]));

  return (
    <div className={styles.pop} onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 40, left: 10 }}>
      <h4>Marcos do cliente</h4>
      <div className="who">{cliente.codigo} — {cliente.nome}</div>
      <ul>
        {estrutura.map(coluna => {
          const aplica = colunaEstado.get(coluna.id)?.aplica ?? true;
          return (
            <li key={coluna.id}>
              <label>
                <input type="checkbox" checked={aplica} onChange={e => onAlternar(coluna.id, e.target.checked)} />
                {coluna.nome}
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default GruposFluxo;
